import { Button, DropdownEditor } from '@approvalmax/ui';
import { selectors, useSortedCompanies } from 'modules/common';
import { PlainDataProvider } from 'modules/data-providers';
import { font } from 'modules/styles';
import { FC, memo, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { routingService } from 'services/routing';
import styled from 'styled-components';
import { getWebsiteUrl } from 'urlBuilder';

import { receiptBankAllowAccess } from '../actions';
import { ReceiptBankAuthModuleData } from '../reducers/page/moduleReducer';
import { getModule } from '../selectors/pageSelectors';
import { getGoBackUrl } from '../selectors/receiptBankSelectors';
import { messages } from './ReceiptBankAuthModule.messages';

const Container = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
`;

const Content = styled.div`
    display: flex;
    flex-flow: column;
    align-items: center;
`;

const Card = styled.div`
    padding: 40px 50px 30px 50px;
    width: 420px;
    background: #fff;
`;

const CardTitle = styled.div`
    text-align: center;
    ${font(16, '#333', 'semibold')}
`;

const CardActions = styled.div`
    margin-top: 30px;

    & > * {
        width: 100%;
    }

    & > :not(:first-child) {
        margin-top: 20px;
    }
`;

const Footer = styled.div`
    margin-top: 40px;
    width: 500px;
    text-align: center;
    ${font(13, '#fff')}
`;

const ReceiptBankAuthModule: FC = () => {
    const dispatch = useDispatch();

    const { callbackUrl } = useSelector(getModule) as ReceiptBankAuthModuleData;
    const companies = useSortedCompanies();
    const myAdminCompanies = useMemo(
        () => companies.filter((company) => company.flags.isManager || company.flags.isWorkflowManager),
        [companies]
    );

    const [selectedCompany, setSelectedCompany] = useState<selectors.types.ExpandedCompany | null>(
        myAdminCompanies[0] || null
    );
    const [loading, setLoading] = useState(false);

    const onAllowAccess = () => {
        setLoading(true);

        try {
            dispatch(receiptBankAllowAccess(selectedCompany!));
        } catch {
            // Only on error
            setLoading(false);
        }
    };

    const onCancel = () => {
        routingService.navigateToExternalUrl(
            getGoBackUrl({
                callbackUrl,
            })
        );
    };

    const allowAccessButton = (
        <Button execute={onAllowAccess} disabled={loading || !selectedCompany} height='large40'>
            {messages.allowAccessButton}
        </Button>
    );
    const backButton = (
        <Button execute={onCancel} disabled={loading} colorTheme='grey' height='large40'>
            {messages.cancelButton}
        </Button>
    );

    let content;

    switch (myAdminCompanies.length) {
        case 0:
            content = (
                <Card>
                    <CardTitle>{messages.cardTitleNoCompanies}</CardTitle>

                    <CardActions>{backButton}</CardActions>
                </Card>
            );
            break;

        case 1:
            content = (
                <Card>
                    <CardTitle>
                        {messages.cardTitleSingleCompany({ companyName: myAdminCompanies[0].displayName })}
                    </CardTitle>

                    <CardActions>
                        {allowAccessButton}

                        {backButton}
                    </CardActions>
                </Card>
            );
            break;

        default:
            content = (
                <Card>
                    <CardTitle>{messages.cardTitleMultipleCompanies}</CardTitle>

                    <PlainDataProvider items={myAdminCompanies} filterAttribute='displayName'>
                        <DropdownEditor
                            css='margin-top: 20px;'
                            buttonComponent={DropdownEditor.NoFilterButton}
                            displayAttribute='displayName'
                            value={selectedCompany}
                            onChange={(v: any) => setSelectedCompany(v)}
                        />
                    </PlainDataProvider>

                    <CardActions>
                        {allowAccessButton}

                        {backButton}
                    </CardActions>
                </Card>
            );
            break;
    }

    return (
        <Container>
            <Content>
                {content}

                <Footer>
                    {messages.footerText({
                        terms: (
                            <a href={`${getWebsiteUrl(false)}terms`} target='_blank' rel='noreferrer'>
                                {messages.terms}
                            </a>
                        ),
                    })}
                </Footer>
            </Content>
        </Container>
    );
};

export default memo(ReceiptBankAuthModule);
